import { Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { ReviewListResponse, ReviewSummary, ReviewTypeCode, UpdateReviewRequest } from '@cart/shared-types';
import { StoreContextService } from '../tenancy/store-context.service';

export interface CreateReviewInput {
  reviewTypeId: string;
  merchantId: string;
  userId: string;
  targetId?: string;
  rating: number;
  title?: string;
  comment?: string;
  attributes?: Record<string, unknown>;
}

export abstract class ReviewsRepository {
  abstract findReviewTypeId(code: ReviewTypeCode): Promise<string | undefined>;
  abstract exists(reviewTypeId: string, userId: string, targetId: string | undefined): Promise<boolean>;
  abstract create(input: CreateReviewInput): Promise<ReviewSummary>;
  abstract update(reviewId: string, userId: string, input: UpdateReviewRequest): Promise<ReviewSummary | undefined>;
  abstract delete(reviewId: string, userId: string): Promise<boolean>;
  abstract list(reviewTypeId: string, targetId: string | undefined): Promise<ReviewListResponse>;
  abstract findMine(reviewTypeId: string, userId: string, targetId: string | undefined): Promise<ReviewSummary | undefined>;
  abstract findMineBatch(reviewTypeId: string, userId: string, targetIds: string[]): Promise<ReviewSummary[]>;
}

const REVIEW_INCLUDE = {
  photos: true,
  reviewType: { select: { code: true } },
} as const;

type ReviewWithRelations = {
  id: string;
  targetId: string | null;
  userId: string;
  rating: number;
  title: string | null;
  comment: string | null;
  attributes: unknown;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  reviewType: { code: string };
  photos: Array<{ id: string; url: string; altText: string | null }>;
};

@Injectable()
export class PrismaReviewsRepository implements ReviewsRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService
  ) {}

  async findReviewTypeId(code: ReviewTypeCode): Promise<string | undefined> {
    const reviewType = await this.prisma.reviewType.findUnique({
      where: { code },
      select: { id: true },
    });

    return reviewType?.id;
  }

  async exists(reviewTypeId: string, userId: string, targetId: string | undefined): Promise<boolean> {
    const review = await this.prisma.review.findFirst({
      where: { reviewTypeId, storeId: this.storeId(), userId, targetId: targetId ?? null },
      select: { id: true },
    });

    return Boolean(review);
  }

  async create(input: CreateReviewInput): Promise<ReviewSummary> {
    const review = await this.prisma.review.create({
      data: {
        reviewTypeId: input.reviewTypeId,
        merchantId: input.merchantId,
        storeId: this.storeId(),
        targetId: input.targetId,
        userId: input.userId,
        rating: input.rating,
        title: input.title,
        comment: input.comment,
        attributes: input.attributes as object | undefined,
        // Reviews publish immediately — moderation (flagging/hiding a review
        // after the fact) is a future admin capability, not a pre-publish gate.
        status: 'approved',
      },
      include: REVIEW_INCLUDE,
    });

    return this.toSummary(review, undefined);
  }

  async update(reviewId: string, userId: string, input: UpdateReviewRequest): Promise<ReviewSummary | undefined> {
    const existing = await this.prisma.review.findFirst({
      where: { id: reviewId, storeId: this.storeId(), userId },
      select: { id: true },
    });

    if (!existing) return undefined;

    const updated = await this.prisma.review.update({
      where: { id: reviewId },
      data: {
        ...(input.rating !== undefined ? { rating: input.rating } : {}),
        ...(input.title !== undefined ? { title: input.title } : {}),
        ...(input.comment !== undefined ? { comment: input.comment } : {}),
        ...(input.attributes !== undefined ? { attributes: input.attributes as object } : {}),
      },
      include: REVIEW_INCLUDE,
    });

    return this.toSummary(updated, undefined);
  }

  async delete(reviewId: string, userId: string): Promise<boolean> {
    const existing = await this.prisma.review.findFirst({
      where: { id: reviewId, storeId: this.storeId(), userId },
      select: { id: true },
    });

    if (!existing) return false;

    await this.prisma.review.delete({ where: { id: reviewId } });

    return true;
  }

  async list(reviewTypeId: string, targetId: string | undefined): Promise<ReviewListResponse> {
    const where = {
      reviewTypeId,
      storeId: this.storeId(),
      status: 'approved',
      ...(targetId !== undefined ? { targetId } : {}),
    };

    const [reviews, aggregate] = await Promise.all([
      this.prisma.review.findMany({
        where,
        include: REVIEW_INCLUDE,
        orderBy: { createdAt: 'desc' },
      }),
      this.prisma.review.aggregate({ where, _avg: { rating: true }, _count: true }),
    ]);

    const userIds = [...new Set(reviews.map((r) => r.userId))];
    const users = userIds.length
      ? await this.prisma.user.findMany({ where: { id: { in: userIds } }, select: { id: true, name: true } })
      : [];
    const userNameById = new Map(users.map((u) => [u.id, u.name]));

    return {
      items: reviews.map((r) => this.toSummary(r, userNameById.get(r.userId))),
      total: aggregate._count,
      averageRating: aggregate._avg.rating ? Math.round(aggregate._avg.rating * 10) / 10 : 0,
    };
  }

  async findMine(reviewTypeId: string, userId: string, targetId: string | undefined): Promise<ReviewSummary | undefined> {
    const review = await this.prisma.review.findFirst({
      where: { reviewTypeId, storeId: this.storeId(), userId, targetId: targetId ?? null },
      include: REVIEW_INCLUDE,
    });

    return review ? this.toSummary(review, undefined) : undefined;
  }

  async findMineBatch(reviewTypeId: string, userId: string, targetIds: string[]): Promise<ReviewSummary[]> {
    if (!targetIds.length) return [];

    const reviews = await this.prisma.review.findMany({
      where: { reviewTypeId, storeId: this.storeId(), userId, targetId: { in: targetIds } },
      include: REVIEW_INCLUDE,
    });

    return reviews.map((r) => this.toSummary(r, undefined));
  }

  private toSummary(review: ReviewWithRelations, userName: string | undefined): ReviewSummary {
    return {
      id: review.id,
      reviewType: review.reviewType.code as ReviewTypeCode,
      targetId: review.targetId ?? undefined,
      userId: review.userId,
      userName,
      rating: review.rating,
      title: review.title ?? undefined,
      comment: review.comment ?? undefined,
      attributes: (review.attributes as Record<string, unknown> | null) ?? undefined,
      status: review.status,
      photos: review.photos.map((p) => ({ id: p.id, url: p.url, altText: p.altText ?? undefined })),
      createdAt: review.createdAt.toISOString(),
      updatedAt: review.updatedAt.toISOString(),
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}
